import { action, observable } from 'mobx'
import { message } from 'antd'
import { cloneDeep, find } from 'lodash'

import { StoreExt } from '@utils/reactExt'
import { Schema$MatchupMarket } from '@protocol/game'
import { IPickList, IPublishPickList, Schema$Pick } from '@protocol/pick'
import { PICK_ESTATE, PICK_LIST_FILTER } from '@interface/common'
import { PICK_TYPE} from 'adv-protocol/src/GameCommon'
import { getPickErrorText } from '@game/errtext'
import { authStore, bettingHistoryStore, globalStore } from '@store/index'
import { replaceMarketName, replaceOutcomeNameSpec } from '@game/util'
import { showToast, TOAST_TYPE } from '@utils/utils'
import { DUPLICATE_REQUEST_EXCEPTION } from '@constants/index'

export type PickListParams = IPickList.Params
export type PickListSchema = IPickList.Schema
export type Pick = Schema$Pick
export type MatchupItem = Schema$MatchupMarket

export { PICK_TYPE, PICK_LIST_FILTER, PICK_ESTATE }

export enum PICK_TRACK_TYPE {
  NONE, // 거래소 - 일반
  PUBLISHED, // 거래소 - 내가 발행한 픽
  PURCHASED, // 거래소 - 내가 구매한 픽

  ONGOING, // 마감 - 진행중
  SUCCESS, // 마감 - 적중
  FAIL, // 마감 - 실패
  EXCEPTION, // 마감 - 적특
}

export interface PickTrack extends Pick {
  dateRegisterInst?: Date
  datePurchaseEndInst?: Date
  dateBettingEndInst?: Date
  myPick: boolean
}

export interface PickListSource {
  index?: number
  tracks?: PickTrack[]
  lastFetched?: PickTrack[]
  hasMore?: boolean
  limit?: number
  total?: number
}

export interface ViewController {
  update: () => void
}

type ViewControllerMap = { [pickId: string]: ViewController }

export class PickStore extends StoreExt {
  // ui indexPickBetSlip
  @observable
  tab = 0

  // ui filter
  @observable
  filter = 0

  @observable
  fetchIndex: number

  // @observable
  sourceMarket: PickListSource

  // @observable
  sourceSelling: PickListSource // success or fail

  // @observable
  sourceBought: PickListSource

  @observable
  publishPickList: IPublishPickList.Schema

  @observable
  purchasePick: Pick

  @observable
  availableTicketBuy: boolean

  // https://github.com/typescript-eslint/typescript-eslint/blob/main/packages/eslint-plugin/docs/rules/ban-types.md
  fetchPickList: () => void

  // 내가 구매한 픽의 컨트롤러
  viewControllers: { [id: string]: ViewControllerMap }

  constructor() {
    super()
    this.cleanup()
  }

  @action
  cleanup = () => {
    this.tab = 0
    this.filter = 0
    this.fetchIndex = undefined
    this.sourceMarket = undefined
    this.sourceSelling = undefined
    this.sourceBought = undefined
    this.publishPickList = undefined
    this.purchasePick = undefined

    this.viewControllers = {}
  }

  addViewController = (index: number, pickId: string, update: () => void) => {
    if (!this.viewControllers[index]) {
      this.viewControllers[index] = {}
    }
    this.viewControllers[index][pickId] = {
      update,
    }
  }

  clearViewControllers = () => {
    this.viewControllers = {}
  }

  updateViewControllerByPickId = (pickId: string, newDividendRate: number) => {
    const trackFound = find(this.sourceBought.tracks, { pickId })
    if (trackFound) {
      trackFound.dividendRate = newDividendRate
      const controller = this.viewControllers[2][pickId]
      if (controller) {
        controller.update()
      }
    }
  }

  @action
  setTab = (i: number): number => {
    this.tab = i
    return i
  }

  @action
  setFilter = (f: number): number => {
    this.filter = f
    return f
  }

  @action
  setPurchasePick = (pick: Pick, availableTicketBuy = false) => {
    this.purchasePick = pick
    this.availableTicketBuy = availableTicketBuy
    return pick
  }

  @action
  buyPick = async (isTicketBuy = false) => {
    try {
      const { pickId } = this.purchasePick
      const { errcode } = await this.api.pick.purchasePick({ pickId, isTicketBuy })
      if (errcode) {
        const errText = getPickErrorText(errcode, '픽 구매를 할 수 없습니다. 새로고침을 해주세요.')
        showToast(TOAST_TYPE.INFO, errText)
        globalStore.pushDialogOk({ title: '알림', text: [errText], className: 'small-size' })
      } else {
        const res = await this.api.user.getUserInfo({})
        const { error } = res
        if (res && error !== DUPLICATE_REQUEST_EXCEPTION) {
          authStore.setUser(res.user)
        }
        return true
      }
    } catch (err) {
      console.error(err)
      message.error('픽 구매 실패! 잠시후 다시 시도해 주세요.')
    }

    return false
  }

  @action
  publishPick = async (betId: string, title: string, price: number, publishType: number) => {
    try {
      const { errcode, pickId } = await this.api.pick.publishPick({ betId, price, title, publishType })
      if (errcode) {
        const errorText = getPickErrorText(errcode, '픽 판매를 할 수 없습니다. 새로고침을 해주세요.')
        showToast(TOAST_TYPE.INFO, errorText)
      } else {
        showToast(TOAST_TYPE.INFO, '픽 판매 등록을 완료했습니다. 픽 판매 시 메시지함으로 하트가 지급됩니다.')
        bettingHistoryStore.pickPublishedOngoing(betId, pickId)
        return true
      }
    } catch (err) {
      console.error(err)
      message.error('픽 판매를 할 수 없습니다. 새로고침을 해주세요.')
    }
    return false
  }

  @action
  controlPick = async (pickId: string, control: number, publishMoneyType: number): Promise<number> => {
    // 0: stop, 1: resume
    try {
      const { errcode, control: newControl } = await this.api.pick.controlPick({ pickId, control, publishMoneyType })
      if (errcode) {
        const errText = getPickErrorText(errcode) || '서버 연결이 원활하지 않습니다. 새로고침해 주세요.'
        globalStore.pushDialogOk({ title: '알림', text: [errText], className: 'small-size' })
      } else {
        return newControl
      }
    } catch (err) {
      console.error(err)
      message.error('서버 연결이 원활하지 않습니다. 새로고침해 주세요.')
    }

    return undefined
  }

  @action
  fetchPublishPick = async (params: IPublishPickList.Params) => {
    try {
      let pickList: IPublishPickList.Schema = { items: [] }
      if (authStore.isSignedIn) {
        pickList = await this.api.pick.getPublishPickList(params)
      }
      this.setPublishPickList(pickList)
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setPublishPickList = (publishPickList: IPublishPickList.Schema): IPublishPickList.Schema => {
    this.publishPickList = publishPickList
    return publishPickList
  }

  // constructor() {
  //   super()
  // }

  // @action
  // fetchPublishPickList

  @action
  fetchItems = async (
    index: number,
    amount: number,
    filter: number,
    order: number,
    resetForce = false,
  ): Promise<PickListSource> => {
    try {
      // state> -1:all, 0:ongoing, 1:success, 2:fail, 3:cancel
      let targetSource = this.sourceMarket
      let target = PICK_TYPE.MARKET
      switch (index) {
        case 0:
          targetSource = this.sourceMarket
          target = PICK_TYPE.MARKET
          break
        case 1:
          targetSource = this.sourceSelling
          target = PICK_TYPE.PUBLISH
          break
        case 2:
          targetSource = this.sourceBought
          target = PICK_TYPE.PURCHASE
          break
      }
      const reset = resetForce === true || this.fetchIndex !== index
      const targetLength = targetSource ? targetSource.tracks.length : 0
      // if (reset === true && targetLength > 0 && index > 0) {
      //   // 탭이 바뀌었지만 이전에 로드해논 아이템이 있으면 재활용(마켓 제외)
      //   return targetSource
      // }
      const offset = reset ? 0 : targetLength
      const { limit, total, items } = await this.api.pick.getPickList({
        target,
        offset,
        limit: amount,
        filter,
        order,
      })

      if (reset) {
        return this.resetTracks(index, { limit, total, items })
      }
      return this.updateTracks(index, { limit, total, items })
    } catch (err) {
      console.error(err)

      message.error('픽 리스트를 정상적으로 불러오지 못하였습니다.')
    }

    return null
  }

  @action
  getSource = fetchIndex => {
    let targetSource
    switch (fetchIndex) {
      case 0:
        targetSource = this.sourceMarket
        break
      case 1:
        targetSource = this.sourceSelling
        break
      case 2:
        targetSource = this.sourceBought
        break
    }
    return targetSource
  }

  @action
  setFetchIndex = (index: number): number => {
    this.fetchIndex = index
    return index
  }

  @action
  resetTracks = (index: number, fetchData: PickListSchema): PickListSource => {
    switch (index) {
      case 0:
        this.sourceMarket = {}
        break
      case 1:
        this.sourceSelling = {}
        break
      case 2:
        this.sourceBought = {}
        break
    }

    return this.updateTracks(index, fetchData)
  }

  @action
  updateTracks = (index: number, fetchData: PickListSchema): PickListSource => {
    let targetSource = this.sourceMarket
    switch (index) {
      case 0:
        targetSource = this.sourceMarket
        break
      case 1:
        targetSource = this.sourceSelling
        break
      case 2:
        targetSource = this.sourceBought
        break
    }
    const { limit, total, items } = fetchData
    const { tracks = [] } = targetSource
    const newTracks: PickTrack[] = cloneDeep(tracks)
    const lastFetched: PickTrack[] = []

    for (const pick of items) {
      if (pick) {
        try {
          const track = cloneDeep(pick) as PickTrack
          track.dateRegisterInst = new Date(pick.dateRegister)
          track.datePurchaseEndInst = new Date(pick.datePurchaseEnd)
          track.dateBettingEndInst = new Date(pick.dateBettingEnd)

          if (track.matches) {
            for (const match of track.matches) {
              for (const fold of match.folds) {
                fold.marketName = replaceMarketName(fold.marketName, match.homeTeamName, match.awayTeamName)
                fold.selectionName = replaceOutcomeNameSpec(fold.selectionName, match.homeTeamName, match.awayTeamName)
                fold.outcomeName = replaceOutcomeNameSpec(fold.outcomeName, match.homeTeamName, match.awayTeamName)
              }
            }
          }

          newTracks.push(track)
          lastFetched.push(track)
        } catch (err) {
          console.error(err)
        }
      }
    }

    const hasMore = newTracks.length < total
    const newSource = { index, tracks: newTracks, lastFetched, hasMore, limit, total }

    switch (index) {
      case 0:
        this.sourceMarket = newSource
        break
      case 1:
        this.sourceSelling = newSource
        break
      case 2:
        this.sourceBought = newSource
        break
    }

    this.setFetchIndex(index)

    return newSource
  }

  getBettingPick = (pickId: string) => {
    const pick = find(this.sourceBought.tracks, { pickId }) as PickTrack
    return pick
  }

  setFetchPickList = (fetchPickList: () => void): void => {
    this.fetchPickList = fetchPickList
  }
}

export default new PickStore()
